import type { Passkey } from "@cleanbudget/contract/employee";
import type { SignInMethods } from "@cleanbudget/contract/session";
import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { Field, FieldError, FieldLabel } from "@cleanbudget/ui/components/field";
import { Input } from "@cleanbudget/ui/components/input";
import { IconCheck, IconKey, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { DetailSection } from "@/components/detail-layout";
import { PageHeader } from "@/components/page-header";
import { AddPasskeyButton } from "@/components/sign-in/add-passkey-button";
import { NewPasswordFields } from "@/components/sign-in/new-password";
import { apiErrorTag } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";
import { orpc } from "@/lib/orpc";

const methodsQuery = () => orpc.session.signInMethods.queryOptions();

/**
 * "Mijn account" (spec, "Feedback op het beheer", D): how the signed-in employee signs in.
 * Passkeys to add on this device or remove, and a password to set, change or remove. The last way
 * in cannot be removed; the API holds to that too.
 */
export const Route = createFileRoute("/_admin/account/")({
	loader: ({ context }) => context.queryClient.ensureQueryData(methodsQuery()),
	component: AccountPage,
});

function AccountPage() {
	const { t } = useTranslation("account");
	const { session } = Route.useRouteContext();
	const { data: methods } = useSuspenseQuery(methodsQuery());

	return (
		<div className="flex max-w-3xl flex-col gap-6">
			<PageHeader title={t("title")} description={`${session.employee.name} · ${session.employee.email}`} />
			<p className="text-sm text-muted-foreground">{t("description")}</p>
			<PasskeysSection methods={methods} employeeId={session.employee.id} />
			<PasswordSection methods={methods} />
		</div>
	);
}

function PasskeysSection({ methods, employeeId }: { methods: SignInMethods; employeeId: string }) {
	const { t } = useTranslation("account");
	const { t: tCommon } = useTranslation();
	const queryClient = useQueryClient();
	const remove = useMutation({
		...orpc.employee.removePasskey.mutationOptions(),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.session.signInMethods.key() }),
	});
	const lastWayIn = methods.passkeys.length <= 1 && !methods.password.set;
	const nameOf = (passkey: Passkey) => t("passkeys.name", { number: methods.passkeys.indexOf(passkey) + 1 });

	const columns: readonly Column<Passkey>[] = [
		{
			id: "passkey",
			header: t("passkeys.column.passkey"),
			cell: (passkey) => (
				<span className="flex flex-col">
					<span className="font-medium">{nameOf(passkey)}</span>
					<span className="text-xs text-muted-foreground">
						{passkey.backedUp ? t("passkeys.synced") : t("passkeys.device")}
					</span>
				</span>
			),
		},
		{
			id: "createdAt",
			header: t("passkeys.column.created"),
			secondary: true,
			cell: (passkey) => <time dateTime={passkey.createdAt}>{formatDateTime(passkey.createdAt)}</time>,
		},
		{
			id: "lastUsedAt",
			header: t("passkeys.column.lastUsed"),
			cell: (passkey) =>
				passkey.lastUsedAt ? (
					<time dateTime={passkey.lastUsedAt}>{formatDateTime(passkey.lastUsedAt)}</time>
				) : (
					<span className="text-muted-foreground">{tCommon("never")}</span>
				),
		},
		{
			id: "actions",
			header: <span className="sr-only">{t("passkeys.column.actions")}</span>,
			align: "right",
			cell: (passkey) => (
				<ConfirmDialog
					trigger={
						<Button
							variant="ghost"
							size="icon-sm"
							disabled={lastWayIn}
							aria-label={t("passkeys.remove.button", { name: nameOf(passkey) })}
						>
							<IconTrash aria-hidden />
						</Button>
					}
					title={t("passkeys.remove.title")}
					description={t("passkeys.remove.description")}
					confirmLabel={t("passkeys.remove.confirm")}
					onConfirm={() => remove.mutateAsync({ id: employeeId, passkeyId: passkey.id })}
					errorMessage={(error) =>
						apiErrorTag(error) === "LastPasskey" ? t("passkeys.lastOne") : tCommon("confirm.failed")
					}
				/>
			),
		},
	];

	return (
		<DetailSection
			title={t("passkeys.title")}
			description={
				lastWayIn && methods.passkeys.length === 1 ? t("passkeys.lastOne") : t("passkeys.description")
			}
			actions={<AddPasskeyButton />}
		>
			<DataTable
				label={t("passkeys.title")}
				columns={columns}
				rows={methods.passkeys}
				rowKey={(passkey) => passkey.id}
				empty={<p className="p-4 text-sm text-muted-foreground">{t("passkeys.none")}</p>}
			/>
		</DetailSection>
	);
}

function PasswordSection({ methods }: { methods: SignInMethods }) {
	const { t } = useTranslation("account");
	const { t: tCommon } = useTranslation();
	const queryClient = useQueryClient();
	const [editing, setEditing] = useState(false);
	const [saved, setSaved] = useState(false);
	const [current, setCurrent] = useState("");
	const [currentWrong, setCurrentWrong] = useState(false);
	const update = (next: SignInMethods) => queryClient.setQueryData(methodsQuery().queryKey, next);
	const setPassword = useMutation({ ...orpc.session.setPassword.mutationOptions(), onSuccess: update });
	const removePassword = useMutation({ ...orpc.session.removePassword.mutationOptions(), onSuccess: update });
	const isSet = methods.password.set;
	const lastWayIn = methods.passkeys.length === 0;

	return (
		<DetailSection
			title={t("password.title")}
			description={isSet && lastWayIn ? t("password.lastOne") : t("password.description")}
		>
			<p className="flex items-center gap-2 text-sm">
				{isSet ? <IconCheck className="size-4 text-emerald-600" aria-hidden /> : null}
				{isSet ? t("password.set") : t("password.notSet")}
				{isSet && methods.password.lastUsedAt ? (
					<span className="text-muted-foreground">
						{t("password.lastUsed", { when: formatDateTime(methods.password.lastUsedAt) })}
					</span>
				) : null}
			</p>
			{saved ? (
				<Alert>
					<IconCheck aria-hidden />
					<AlertDescription>{t("password.saved")}</AlertDescription>
				</Alert>
			) : null}
			{editing ? (
				<div className="flex max-w-sm flex-col gap-4">
					<NewPasswordFields
						submitLabel={isSet ? t("password.change") : t("password.choose")}
						otherError={(error) =>
							apiErrorTag(error) === "CurrentPasswordWrong"
								? t("password.currentWrong")
								: tCommon("form.failed")
						}
						onSave={async (password) => {
							setCurrentWrong(false);
							try {
								await setPassword.mutateAsync({ password, ...(isSet ? { currentPassword: current } : {}) });
							} catch (error) {
								if (apiErrorTag(error) === "CurrentPasswordWrong") setCurrentWrong(true);
								throw error;
							}
							setEditing(false);
							setCurrent("");
							setSaved(true);
						}}
					>
						{isSet ? (
							<CurrentPasswordField value={current} onChange={setCurrent} invalid={currentWrong} />
						) : null}
					</NewPasswordFields>
					<Button variant="ghost" onClick={() => setEditing(false)}>
						{t("password.cancel")}
					</Button>
				</div>
			) : (
				<div className="flex flex-wrap gap-2">
					<Button
						variant="outline"
						onClick={() => {
							setSaved(false);
							setEditing(true);
						}}
					>
						<IconKey aria-hidden />
						{isSet ? t("password.change") : t("password.choose")}
					</Button>
					{isSet ? (
						<ConfirmDialog
							trigger={
								<Button variant="ghost" disabled={lastWayIn}>
									<IconTrash aria-hidden />
									{t("password.remove.button")}
								</Button>
							}
							title={t("password.remove.title")}
							description={t("password.remove.description")}
							confirmLabel={t("password.remove.confirm")}
							onConfirm={() => removePassword.mutateAsync({})}
							errorMessage={(error) =>
								apiErrorTag(error) === "LastSignInMethod" ? t("password.lastOne") : tCommon("confirm.failed")
							}
						/>
					) : null}
				</div>
			)}
		</DetailSection>
	);
}

/** The current password, asked for before a change; outside the new-password form's own schema. */
function CurrentPasswordField({
	value,
	onChange,
	invalid,
}: {
	value: string;
	onChange: (value: string) => void;
	invalid: boolean;
}) {
	const { t } = useTranslation("account");
	const id = useId();
	return (
		<Field data-invalid={invalid || undefined}>
			<FieldLabel htmlFor={id}>{t("password.current")}</FieldLabel>
			<Input
				id={id}
				type="password"
				autoComplete="current-password"
				value={value}
				onChange={(event) => onChange(event.target.value)}
				aria-invalid={invalid || undefined}
				aria-describedby={invalid ? `${id}-error` : undefined}
			/>
			<FieldError id={`${id}-error`}>{invalid ? t("password.currentWrong") : undefined}</FieldError>
		</Field>
	);
}
